import React from 'react';

export const CONTACT_EMAIL = 'hello@speakparrot.com';
export const EFFECTIVE_DATE = 'May 20, 2026';

export type LegalSection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

const linkClass = 'text-parrot-blue underline underline-offset-2 hover:text-parrot-blue-deep';

const MailLink = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
    {CONTACT_EMAIL}
  </a>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a href={href} target="_blank" rel="noreferrer" className={linkClass}>
    {children}
  </a>
);

const LegalList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="mt-3 list-disc space-y-2 pl-6">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

export const termsSections: LegalSection[] = [
  {
    id: 'terms-acceptance',
    title: '1. Acceptance of Terms',
    content: (
      <>
        <p>
          These Terms of Service ("Terms") are a legal agreement between you
          and Parrot ("Parrot", "we", "us", or "our") governing your access to
          and use of the Parrot mobile application, website, and related
          services (collectively, the "Service").
        </p>
        <p>
          By downloading, installing, creating an account, subscribing to, or
          using the Service, you agree to these Terms. If you do not agree, do
          not use the Service.
        </p>
      </>
    ),
  },
  {
    id: 'terms-service',
    title: '2. The Service',
    content: (
      <p>
        Parrot helps users practice short speaking exercises, receive automated
        transcription, scoring, feedback, progress tracking, vocabulary prompts,
        reminders, and optional challenge or face-off experiences. The Service
        is provided for practice and personal development only. It is not a
        professional coaching certification, medical service, legal service, or
        guarantee of any speaking outcome.
      </p>
    ),
  },
  {
    id: 'terms-eligibility',
    title: '3. Eligibility and Accounts',
    content: (
      <>
        <p>
          You must be at least 13 years old to use Parrot. If you are under the
          age of majority where you live, you may use the Service only with
          permission from a parent or legal guardian.
        </p>
        <p>
          You are responsible for keeping your account credentials secure and
          for all activity under your account. You agree to provide accurate
          account information and to update it when needed.
        </p>
      </>
    ),
  },
  {
    id: 'terms-subscriptions',
    title: '4. Subscriptions, Trials, and Billing',
    content: (
      <>
        <p>
          Parrot may offer paid subscription plans, including weekly, monthly,
          annual, promotional, or trial plans. Subscription details, trial
          length, pricing, renewal timing, and cancellation terms are shown at
          the point of purchase.
        </p>
        <p>
          Purchases made in the iOS app are processed by Apple through your
          Apple ID. Subscriptions automatically renew unless canceled at least
          24 hours before the end of the current billing period. You can manage
          or cancel your subscription in your Apple ID subscription settings.
          Refund requests for Apple purchases are handled by Apple under
          Apple's policies.
        </p>
        <p>
          Web purchases and promotional web offers may be processed by Stripe.
          Stripe manages the card checkout, billing record, renewal, and
          payment method details for those web purchases.
        </p>
      </>
    ),
  },
  {
    id: 'terms-content',
    title: '5. Your Content',
    content: (
      <>
        <p>
          You keep ownership of recordings, transcripts, prompts you answer,
          score results, profile details, and other content you submit or
          create through the Service ("User Content").
        </p>
        <p>
          You grant Parrot a limited, worldwide, non-exclusive, royalty-free
          license to host, store, process, transcribe, analyze, display, and
          otherwise use User Content as needed to operate, maintain, improve,
          secure, and support the Service. This license ends when your User
          Content is deleted from our active systems, except where retention is
          required for legal, security, backup, or legitimate business purposes.
        </p>
      </>
    ),
  },
  {
    id: 'terms-sharing',
    title: '6. Sharing and Challenges',
    content: (
      <p>
        Recordings and practice results are private by default. If you use a
        sharing, challenge, invite, or face-off feature, you are responsible for
        the content you choose to share and for making sure you have permission
        to share any personal information or recordings included in it.
      </p>
    ),
  },
  {
    id: 'terms-ai',
    title: '7. Automated Feedback',
    content: (
      <p>
        Parrot uses automated systems to transcribe, score, summarize, and
        provide feedback. Automated outputs may be incomplete, inaccurate, or
        inappropriate for your situation. You are responsible for evaluating
        feedback before relying on it.
      </p>
    ),
  },
  {
    id: 'terms-use-rules',
    title: '8. Acceptable Use',
    content: (
      <>
        <p>You agree not to:</p>
        <LegalList
          items={[
            'Use the Service for unlawful, harmful, deceptive, abusive, or infringing activity.',
            'Upload content that violates another person\'s rights or privacy.',
            'Attempt to access accounts, systems, data, or code without authorization.',
            'Reverse engineer, scrape, overload, disrupt, or interfere with the Service.',
            'Use the Service to create or distribute spam, malware, or misleading content.',
            'Misrepresent your identity or subscription status.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'terms-ip',
    title: '9. Parrot Intellectual Property',
    content: (
      <p>
        The Service, including the Parrot name, logo, design, software,
        prompts, interfaces, copy, graphics, scoring systems, and other
        materials, is owned by Parrot or its licensors and is protected by
        intellectual property laws. We grant you a limited, revocable,
        non-transferable license to use the Service for personal, non-commercial
        purposes in accordance with these Terms.
      </p>
    ),
  },
  {
    id: 'terms-third-party',
    title: '10. Third-Party Services',
    content: (
      <p>
        The Service may rely on or link to third-party services, including app
        stores, authentication providers, payment processors, analytics,
        hosting, cloud infrastructure, email, transcription, and notification
        providers. We are not responsible for third-party services, and your use
        of them may be subject to their own terms and privacy policies.
      </p>
    ),
  },
  {
    id: 'terms-apple',
    title: '11. Apple Terms',
    content: (
      <>
        <p>
          If you access Parrot through an Apple device or download it from the
          Apple App Store, your use of the app is also subject to Apple's
          Standard End User License Agreement unless we provide a custom EULA.
          You can review Apple's Standard EULA here:{' '}
          <ExternalLink href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/">
            Apple Standard EULA
          </ExternalLink>
          .
        </p>
        <p>
          Apple is not responsible for providing maintenance or support for the
          Service. To the maximum extent permitted by law, Apple has no warranty
          obligation for the Service. Apple and its subsidiaries are third-party
          beneficiaries of these Terms and may enforce them against you.
        </p>
      </>
    ),
  },
  {
    id: 'terms-privacy',
    title: '12. Privacy',
    content: (
      <p>
        Our Privacy Policy explains how we collect, use, share, retain, and
        protect information. By using the Service, you also agree to our Privacy
        Policy, which is available at speakparrot.com/legal#privacy and
        speakparrot.com/privacy.
      </p>
    ),
  },
  {
    id: 'terms-termination',
    title: '13. Suspension and Termination',
    content: (
      <p>
        We may suspend or terminate access to the Service if we believe you
        violated these Terms, created risk for Parrot or others, or used the
        Service in a way that could cause harm or legal exposure. You may stop
        using the Service at any time and may delete your account in the app or
        by contacting us.
      </p>
    ),
  },
  {
    id: 'terms-disclaimer',
    title: '14. Disclaimers',
    content: (
      <p>
        The Service is provided "as is" and "as available." To the maximum
        extent permitted by law, Parrot disclaims all warranties, express or
        implied, including warranties of merchantability, fitness for a
        particular purpose, title, and non-infringement. We do not guarantee
        that the Service will be uninterrupted, secure, error-free, or that any
        transcription, score, feedback, or recommendation will be accurate.
      </p>
    ),
  },
  {
    id: 'terms-liability',
    title: '15. Limitation of Liability',
    content: (
      <p>
        To the maximum extent permitted by law, Parrot and its owners,
        employees, contractors, affiliates, service providers, and licensors
        will not be liable for indirect, incidental, special, consequential,
        exemplary, or punitive damages, or for lost profits, lost data, lost
        goodwill, service interruption, or business interruption arising out of
        or related to the Service or these Terms.
      </p>
    ),
  },
  {
    id: 'terms-indemnity',
    title: '16. Indemnification',
    content: (
      <p>
        You agree to defend, indemnify, and hold harmless Parrot and its owners,
        employees, contractors, affiliates, service providers, and licensors from
        claims, damages, liabilities, costs, and expenses arising from your use
        of the Service, your User Content, your violation of these Terms, or
        your violation of another person's rights.
      </p>
    ),
  },
  {
    id: 'terms-law',
    title: '17. Governing Law',
    content: (
      <p>
        These Terms are governed by the laws of the United States and, where
        applicable, the laws of the state in which Parrot maintains its
        principal place of business, without regard to conflict-of-law rules.
        Your local consumer protection laws may also apply.
      </p>
    ),
  },
  {
    id: 'terms-changes',
    title: '18. Changes to These Terms',
    content: (
      <p>
        We may update these Terms from time to time. If we make material
        changes, we will provide notice through the Service, by email, or by
        updating the effective date above. Continued use of the Service after
        changes become effective means you accept the updated Terms.
      </p>
    ),
  },
  {
    id: 'terms-contact',
    title: '19. Contact',
    content: (
      <p>
        Questions about these Terms can be sent to <MailLink />.
      </p>
    ),
  },
];

export const privacySections: LegalSection[] = [
  {
    id: 'privacy-overview',
    title: '1. Overview',
    content: (
      <p>
        This Privacy Policy explains how Parrot collects, uses, shares, retains,
        and protects information when you use the Parrot mobile application,
        website, support channels, and related services (collectively, the
        "Service").
      </p>
    ),
  },
  {
    id: 'privacy-collect',
    title: '2. Information We Collect',
    content: (
      <>
        <p>We may collect the following categories of information:</p>
        <LegalList
          items={[
            <span>
              <strong>Account information:</strong> email address, display
              name, username, authentication identifiers, sign-in method, and
              account settings.
            </span>,
            <span>
              <strong>Speech practice content:</strong> recordings,
              transcripts, prompts, categories, Word of the Day usage, scores,
              feedback, summaries, saved attempts, challenge results, and
              face-off invite details.
            </span>,
            <span>
              <strong>Subscription information:</strong> product identifiers,
              entitlement status, renewal status, purchase history, restore
              status, and other App Store or RevenueCat subscription metadata.
              We do not receive your full payment card number.
            </span>,
            <span>
              <strong>Device and technical information:</strong> device type,
              operating system, app version, device identifiers, push
              notification tokens, language, approximate network information,
              logs, diagnostics, and crash-related information.
            </span>,
            <span>
              <strong>Usage and analytics information:</strong> app launches,
              onboarding progress, paywall interactions, selected plans,
              practice events, scores, sharing events, notification
              interactions, and similar product analytics.
            </span>,
            <span>
              <strong>Support communications:</strong> messages, screenshots,
              email address, device details, and other information you send us
              when requesting help.
            </span>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'privacy-audio',
    title: '3. Audio, Transcription, and Automated Scoring',
    content: (
      <>
        <p>
          Parrot is built around speech practice, so recordings and transcripts
          are core parts of the Service. Speech recognition and scoring may run
          on your device when supported. In those cases, the analysis step can
          happen locally.
        </p>
        <p>
          Some features may transmit speech content to our service providers,
          including when a recording is saved to your account, synced across
          devices, shared through a challenge or face-off, or sent for cloud
          transcription repair when the on-device transcript fails or you choose
          to re-transcribe with AI.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-use',
    title: '4. How We Use Information',
    content: (
      <>
        <p>We use information to:</p>
        <LegalList
          items={[
            'Provide, operate, personalize, and improve the Service.',
            'Create accounts, authenticate users, sync progress, and support account deletion.',
            'Record, transcribe, score, summarize, and display speech practice results.',
            'Process subscriptions, trials, entitlement checks, restorations, and customer support.',
            'Send reminders, push notifications, service messages, support replies, and optional emails.',
            'Analyze feature performance, troubleshoot bugs, prevent abuse, and protect the Service.',
            'Comply with legal obligations and enforce our Terms.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'privacy-share',
    title: '5. How We Share Information',
    content: (
      <>
        <p>
          We do not sell your personal information. We share information only as
          needed for the purposes described in this policy, including with:
        </p>
        <LegalList
          items={[
            <span>
              <strong>Service providers:</strong> companies that provide cloud
              hosting, authentication, databases, file storage, analytics,
              attribution, transcription, subscriptions, push notifications,
              email, customer support, and infrastructure.
            </span>,
            <span>
              <strong>Apple and payment platforms:</strong> to process App
              Store subscriptions, trials, receipts, refunds, renewals, and
              entitlement status.
            </span>,
            <span>
              <strong>Other users you choose to interact with:</strong> for
              example, when you use sharing, challenge, invite, or face-off
              features.
            </span>,
            <span>
              <strong>Legal, safety, and business purposes:</strong> when
              required by law, to protect rights and safety, to enforce our
              Terms, or in connection with a merger, financing, acquisition, or
              sale of assets.
            </span>,
          ]}
        />
        <p>
          We may use vendors such as Firebase/Google, RevenueCat, Apple,
          OpenAI, AppsFlyer, Netlify, and email or messaging providers to
          operate the Service. We require service providers to process
          information only for permitted purposes.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-tracking',
    title: '6. Advertising, Analytics, and Tracking',
    content: (
      <p>
        We may use analytics and attribution tools to understand how users find
        and use Parrot, measure subscriptions, and improve the Service. Where
        required by law or platform rules, we will ask for permission before
        using information to track you across apps or websites owned by other
        companies. You can manage platform privacy controls in iOS Settings.
      </p>
    ),
  },
  {
    id: 'privacy-permissions',
    title: '7. Device Permissions and Choices',
    content: (
      <>
        <p>
          Parrot may request access to microphone, speech recognition,
          notifications, and other device features. You can change device
          permissions in iOS Settings. If you turn off a permission, some
          features may not work.
        </p>
        <p>
          You can manage notification preferences in the app or iOS Settings.
          You can unsubscribe from non-essential emails using the unsubscribe
          link where available or by contacting us.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-retention',
    title: '8. Data Retention',
    content: (
      <p>
        We retain information for as long as needed to provide the Service,
        maintain your account, comply with legal obligations, resolve disputes,
        prevent abuse, and enforce our agreements. Saved recordings,
        transcripts, and account data are generally retained while your account
        is active. Backup copies, logs, subscription records, and support
        records may be retained for a limited period after deletion when needed
        for legal, security, accounting, fraud-prevention, or operational
        reasons.
      </p>
    ),
  },
  {
    id: 'privacy-delete',
    title: '9. Account Deletion and Privacy Requests',
    content: (
      <>
        <p>
          You can delete your Parrot account from within the app where account
          deletion is available. Account deletion removes your account record
          and saved recordings from active systems, subject to the retention
          limits described above.
        </p>
        <p>
          You can also request access, correction, deletion, export, or other
          privacy assistance by emailing <MailLink /> from the email address
          associated with your account. We may need to verify your identity
          before completing a request.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-children',
    title: "10. Children's Privacy",
    content: (
      <p>
        Parrot is not directed to children under 13, and we do not knowingly
        collect personal information from children under 13. If you believe a
        child has provided personal information to Parrot, contact us and we
        will take appropriate steps to delete it.
      </p>
    ),
  },
  {
    id: 'privacy-state',
    title: '11. State and Regional Privacy Rights',
    content: (
      <p>
        Depending on where you live, you may have rights to know, access,
        correct, delete, obtain a copy of, or opt out of certain processing of
        your personal information. We do not sell personal information. To make
        a privacy request, contact <MailLink />. We will respond as required by
        applicable law.
      </p>
    ),
  },
  {
    id: 'privacy-security',
    title: '12. Security',
    content: (
      <p>
        We use reasonable technical, administrative, and organizational
        safeguards designed to protect information. No internet or mobile
        service can be guaranteed to be completely secure, so you use the
        Service at your own risk.
      </p>
    ),
  },
  {
    id: 'privacy-transfer',
    title: '13. International Transfers',
    content: (
      <p>
        We and our service providers may process information in the United
        States and other countries. These countries may have data protection
        laws different from those where you live.
      </p>
    ),
  },
  {
    id: 'privacy-changes',
    title: '14. Changes to This Policy',
    content: (
      <p>
        We may update this Privacy Policy from time to time. If we make
        material changes, we will provide notice through the Service, by email,
        or by updating the effective date above. Continued use of the Service
        after the updated policy takes effect means you acknowledge the updated
        policy.
      </p>
    ),
  },
  {
    id: 'privacy-contact',
    title: '15. Contact',
    content: (
      <p>
        For privacy questions, account deletion support, or data requests,
        contact <MailLink />.
      </p>
    ),
  },
];
